import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, ready, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (ready && user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050A1F] text-white" data-testid="admin-login-page">
      <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_50%_-10%,rgba(212,175,55,0.12),transparent_60%)]" />
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-white/60 hover:text-[#D4AF37]"
          data-testid="login-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
        </Link>

        <Logo />

        <div className="mt-10 border border-white/10 bg-[#0A1128] p-8">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Admin Portal</div>
          <h1 className="mt-3 font-serif text-3xl font-light">Sign in.</h1>
          <p className="mt-2 text-sm text-white/60">
            Restricted access. Administrators only.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="email" className="text-[10px] uppercase tracking-[0.28em] text-white/60">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="mt-2 w-full border border-white/15 bg-[#050A1F] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                placeholder="admin@lendingloansusa.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="text-[10px] uppercase tracking-[0.28em] text-white/60">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="mt-2 w-full border border-white/15 bg-[#050A1F] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="inline-flex w-full items-center justify-center gap-3 border border-[#D4AF37] bg-[#D4AF37] px-6 py-3.5 text-xs uppercase tracking-[0.32em] text-[#050A1F] transition-colors hover:bg-[#F3E5AB] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/40">
            <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />
            <span>256-bit Encrypted Session</span>
          </div>
        </div>
      </div>
    </div>
  );
}
